import * as yup from 'yup';
import {
    Address,
    Case,
    CaseDocument,
    CaseEngagementDoc,
    CaseEvent,
    CaseEventTemplate,
    CaseExpense,
    CaseMilestone,
    CaseMilestoneTemplate,
    CaseStatus,
    CaseSystemStatus,
    CaseTemplate,
    Contact,
    EngagementTemplate,
    FieldEntry,
    Individual,
    Invoice,
    Note,
    Party,
    PartyWithContacts,
    Phone,
    Task,
    TaskStatus,
    TaskTemplate,
    TimeTrackingEntry,
    User,
} from './commonTypes';
import { Account, AccountSettings, FeatureSet, MetricDisplay } from './authTypes';
import { FileUploadInfo } from '../services/FileUploadManager';

export type ExpenseWithAttachment = { attachment?: File | null; expense: CaseExpense };

export const useValidation = () => {
    /**
     * These values are sent as URL path segments. A blank value collapses the segment and a dot-only
     * value ("." / "..") is stripped by URL normalization, so the request never reaches the endpoint.
     */
    const pathSegmentSafe = <T extends yup.StringSchema>(schema: T): T =>
        schema
            .test('not-blank', 'Cannot be blank', (value) => value === undefined || !!value.trim())
            .test(
                'not-dot-segment',
                'Cannot be only periods',
                (value) => value === undefined || !/^\.+$/.test(value.trim()),
            );

    //Address constants
    const ADDRESS1_MAX_LENGTH = 150;
    const ADDRESS2_MAX_LENGTH = 50;
    const CITY_MAX_LENGTH = 75;
    const STATE_MAX_LENGTH = 5;
    const COUNTRY_MAX_LENGTH = 50;
    const ZIP_MAX_LENGTH = 12;
    const ZIP_PATTERN = /^[A-Z0-9 -]*$/;
    const CITY_STATE_PATTERN = /^[A-Z .'-]*$/;
    const CITY_STATE_MESSAGE = "Only letters, spaces, hyphens, apostrophes, and periods are allowed";

    const validateAddress = {
        schema: yup.object<Address>({
            address1: yup.string().max(ADDRESS1_MAX_LENGTH, `Max ${ADDRESS1_MAX_LENGTH} characters`).nullable(),
            address2: yup.string().max(ADDRESS2_MAX_LENGTH, `Max ${ADDRESS2_MAX_LENGTH} characters`).nullable(),
            city: yup
                .string()
                .uppercase()
                .max(CITY_MAX_LENGTH, `Max ${CITY_MAX_LENGTH} characters`)
                .matches(CITY_STATE_PATTERN, CITY_STATE_MESSAGE)
                .nullable(),
            state: yup
                .string()
                .uppercase()
                .max(STATE_MAX_LENGTH, `Max ${STATE_MAX_LENGTH} characters`)
                .matches(CITY_STATE_PATTERN, CITY_STATE_MESSAGE)
                .nullable(),
            country: yup.string().max(COUNTRY_MAX_LENGTH, `Max ${COUNTRY_MAX_LENGTH} characters`).nullable(),
            zip: yup
                .string()
                .uppercase()
                .max(ZIP_MAX_LENGTH, `Max ${ZIP_MAX_LENGTH} characters`)
                .matches(ZIP_PATTERN, 'Only letters, numbers, spaces, and hyphens are allowed')
                .nullable(),
        }),
        address1: {
            maxLength: ADDRESS1_MAX_LENGTH,
        },
        address2: {
            maxLength: ADDRESS2_MAX_LENGTH,
        },
        city: {
            maxLength: CITY_MAX_LENGTH,
            pattern: CITY_STATE_PATTERN,
        },
        state: {
            maxLength: STATE_MAX_LENGTH,
            pattern: CITY_STATE_PATTERN,
        },
        country: {
            maxLength: COUNTRY_MAX_LENGTH,
        },
        zip: {
            maxLength: ZIP_MAX_LENGTH,
            pattern: ZIP_PATTERN,
        },
    };

    //Phone constants
    const PHONE_NUMBER_MAX_LENGTH = 20;

    const validatePhone = {
        schema: yup.object<Phone>({
            number: yup
                .string()
                .max(PHONE_NUMBER_MAX_LENGTH, `Max ${PHONE_NUMBER_MAX_LENGTH} characters`)
                .matches(/^\+?[0-9\s\-\(\)]{7,20}$/, 'Invalid phone number'),
            primary: yup.boolean(),
        }),
        number: {
            maxLength: PHONE_NUMBER_MAX_LENGTH,
        },
    };

    //Account constants
    const ACCOUNT_NAME_MAX_LENGTH = 50;
    const ACCOUNT_EMAIL_MAX_LENGTH = 254;
    const ACCOUNT_PHONE_MAX_LENGTH = 20;
    const ACCOUNT_WEBSITE_MAX_LENGTH = 1500;

    const validateAccount = {
        schema: yup.object<Account>({
            accountName: yup
                .string()
                .max(ACCOUNT_NAME_MAX_LENGTH, `Max ${ACCOUNT_NAME_MAX_LENGTH} characters`)
                .required('Account Name is required.'),
            // email: yup.string().email().max(ACCOUNT_EMAIL_MAX_LENGTH, `Max ${ACCOUNT_EMAIL_MAX_LENGTH} characters`).required(),
            phone: validatePhone.schema,
            website: yup.string().max(ACCOUNT_WEBSITE_MAX_LENGTH, `Max ${ACCOUNT_WEBSITE_MAX_LENGTH} characters`).nullable(),
            settings: yup.object<AccountSettings>(),
            address: validateAddress.schema,
        }),
        accountName: { maxLength: ACCOUNT_NAME_MAX_LENGTH },
        email: { maxLength: ACCOUNT_EMAIL_MAX_LENGTH },
        phone: { maxLength: ACCOUNT_PHONE_MAX_LENGTH },
        website: { maxLength: ACCOUNT_WEBSITE_MAX_LENGTH },
    };

    //Individual constants
    const PERSON_NAME_MAX_LENGTH = 150;
    const PERSON_TITLE_MAX_LENGTH = 75;
    const PERSON_EMAIL_MAX_LENGTH = 254;

    const validateIndividual = {
        schema: yup.object<Individual>({
            name: yup.string().max(PERSON_NAME_MAX_LENGTH, `Max ${PERSON_NAME_MAX_LENGTH} characters`),
            title: yup.string().max(PERSON_TITLE_MAX_LENGTH, `Max ${PERSON_TITLE_MAX_LENGTH} characters`).nullable(),
            address: validateAddress.schema,
            email: yup.string().email().max(PERSON_EMAIL_MAX_LENGTH, `Max ${PERSON_EMAIL_MAX_LENGTH} characters`),
        }),
        name: { maxLength: PERSON_NAME_MAX_LENGTH },
        title: { maxLength: PERSON_TITLE_MAX_LENGTH },
        email: { maxLength: PERSON_EMAIL_MAX_LENGTH },
    };

    const validateUser = {
        schema: yup.object<User>({
            name: yup
                .string()
                .max(PERSON_NAME_MAX_LENGTH, `Max ${PERSON_NAME_MAX_LENGTH} characters`)
                .required('Name is a required field.'),
            username: yup
                .string()
                .max(PERSON_EMAIL_MAX_LENGTH, `Max ${PERSON_EMAIL_MAX_LENGTH} characters`)
                .required('Email/Username is a required field.'),
            email: yup.string().email().max(PERSON_EMAIL_MAX_LENGTH, `Max ${PERSON_EMAIL_MAX_LENGTH} characters`),
            phone: validatePhone.schema,
            expectedHours: yup
                .number()
                .min(0, 'Expected hours must be a positive number')
                .max(24, 'Expected hours cannot exceed 24')
                .nullable(),
            address: validateAddress.schema,
        }),
        name: { maxLength: PERSON_NAME_MAX_LENGTH },
        username: { maxLength: PERSON_EMAIL_MAX_LENGTH },
        email: { maxLength: PERSON_EMAIL_MAX_LENGTH },
        phone: { maxLength: PHONE_NUMBER_MAX_LENGTH },
    };

    const validateContact = {
        schema: yup.object<Contact>({
            name: yup
                .string()
                .max(PERSON_NAME_MAX_LENGTH, `Max ${PERSON_NAME_MAX_LENGTH} characters`)
                .required('Name is a required field.'),
            title: yup
                .string()
                .max(PERSON_TITLE_MAX_LENGTH, `Max ${PERSON_TITLE_MAX_LENGTH} characters`)
                .nullable(),
            address: validateAddress.schema,
            phone: yup.array(validatePhone.schema),
            email: yup
                .string()
                .email()
                .max(PERSON_EMAIL_MAX_LENGTH, `Max ${PERSON_EMAIL_MAX_LENGTH} characters`),
        }),
    };

    //Party constants
    const PARTY_NAME_MAX_LENGTH = 150;
    const PARTY_URL_MAX_LENGTH = 1500;

    const validateParty = {
        schema: yup.object<Party>({
            name: yup
                .string()
                .max(PARTY_NAME_MAX_LENGTH, `Max ${PARTY_NAME_MAX_LENGTH} characters`)
                .required('Name is a required field.'),
            address: validateAddress.schema,
            phone: yup.array(validatePhone.schema),
            url: yup.string().max(PARTY_URL_MAX_LENGTH, `Max ${PARTY_URL_MAX_LENGTH} characters`).nullable(),
            logoUrl: yup.string().max(PARTY_URL_MAX_LENGTH, `Max ${PARTY_URL_MAX_LENGTH} characters`).nullable(),
        }),
        name: { maxLength: PARTY_NAME_MAX_LENGTH },
        url: { maxLength: PARTY_URL_MAX_LENGTH },
        logoUrl: { maxLength: PARTY_URL_MAX_LENGTH },
    };

    const validatePartyWithContacts = {
        schema: yup.object<PartyWithContacts>({
            party: validateParty.schema,
            contacts: yup.array(validateContact.schema).required(),
        }),
    };

    //FieldEntry constants
    const FIELD_NAME_MAX_LENGTH = 50;
    const FIELD_VALUE_MAX_LENGTH = 5000;

    const fieldEntrySchema = (valLength?: number) =>
        yup.object<FieldEntry>({
            fieldName: yup.string().max(FIELD_NAME_MAX_LENGTH, `Max ${FIELD_NAME_MAX_LENGTH} characters`).nullable(),
            fieldValue: yup
                .string()
                .max(valLength || FIELD_VALUE_MAX_LENGTH, `Max ${valLength || FIELD_VALUE_MAX_LENGTH} characters`)
                .nullable(),
        });
    const validateFieldEntry = {
        schema: fieldEntrySchema,
        fieldName: { maxLength: FIELD_NAME_MAX_LENGTH },
        fieldValue: { maxLength: FIELD_VALUE_MAX_LENGTH },
    };

    //Case Status constants
    const CASE_STATUS_STATUS_MAX_LENGTH = 50;
    const CASE_STATUS_DESCRIPTION_MAX_LENGTH = 200;

    const validateCaseStatus = {
        schema: yup.object<CaseStatus>({
            status: pathSegmentSafe(
                yup
                    .string()
                    .max(CASE_STATUS_STATUS_MAX_LENGTH, `Max ${CASE_STATUS_STATUS_MAX_LENGTH} characters`)
                    .matches(/^[a-zA-Z0-9\s\-_.\/]+$/, 'Contains invalid characters'),
            ).required(),
            systemStatus: yup
                .mixed<CaseSystemStatus>()
                .oneOf(Object.values(CaseSystemStatus) as CaseSystemStatus[])
                .required(),
            description: yup
                .string()
                .max(CASE_STATUS_DESCRIPTION_MAX_LENGTH, `Max ${CASE_STATUS_DESCRIPTION_MAX_LENGTH} characters`)
                .nullable(),
            order: yup.number(),
        }),
        status: { maxLength: CASE_STATUS_STATUS_MAX_LENGTH },
        description: { maxLength: CASE_STATUS_DESCRIPTION_MAX_LENGTH },
    };

    //Case Event constants
    const CASE_EVENT_EVENT_MAX_LENGTH = 100;
    const CASE_EVENT_DESCRIPTION_MAX_LENGTH = 500;

    const validateCaseEvent = {
        schema: yup.object<CaseEvent>({
            event: yup
                .string()
                .max(CASE_EVENT_EVENT_MAX_LENGTH, `Max ${CASE_EVENT_EVENT_MAX_LENGTH} characters`)
                .matches(/^[a-zA-Z0-9\s\-_.,]+$/, 'Contains invalid characters')
                .required('Event is a required field.'),
            date: yup.date().required(),
            due: yup.boolean(),
            description: yup
                .string()
                .max(CASE_EVENT_DESCRIPTION_MAX_LENGTH, `Max ${CASE_EVENT_DESCRIPTION_MAX_LENGTH} characters`)
                .nullable(),
            hasNotes: yup.boolean(),
        }),
        event: { maxLength: CASE_EVENT_EVENT_MAX_LENGTH },
        description: { maxLength: CASE_EVENT_DESCRIPTION_MAX_LENGTH },
    };

    //Case Note constants
    const CASE_NOTE_TEXT_MAX_LENGTH = 2000;

    const validateCaseNote = {
        schema: yup.object<Note>({
            text: yup.string().max(CASE_NOTE_TEXT_MAX_LENGTH, `Max ${CASE_NOTE_TEXT_MAX_LENGTH} characters`),
        }),
        text: { maxLength: CASE_NOTE_TEXT_MAX_LENGTH },
    };

    // Document constants
    const DOCUMENT_NAME_MAX_LENGTH = 150;
    const DOCUMENT_DESCRIPTION_MAX_LENGTH = 500;
    const DOCUMENT_SUMMARY_MAX_LENGTH = 3000;

    const validateCaseDocument = {
        schema: yup.object<CaseDocument>({
            accountId: yup.string(),
            caseId: yup.string(),
            docId: yup.string(),
            name: yup
                .string()
                .max(DOCUMENT_NAME_MAX_LENGTH, `Max ${DOCUMENT_NAME_MAX_LENGTH} characters`)
                .matches(/^[a-zA-Z0-9\s\-_.()]+$/, 'Contains invalid characters')
                .required(),
            description: yup
                .string()
                .max(DOCUMENT_DESCRIPTION_MAX_LENGTH, `Max ${DOCUMENT_DESCRIPTION_MAX_LENGTH} characters`)
                .nullable(),
            summary: yup
                .string()
                .max(DOCUMENT_SUMMARY_MAX_LENGTH, `Max ${DOCUMENT_SUMMARY_MAX_LENGTH} characters`)
                .nullable(),
            size: yup.number(),
            uploaded: yup.boolean(),
            hasNotes: yup.boolean(),
            isDeleted: yup.boolean(),
        }),
        name: {
            maxLength: DOCUMENT_NAME_MAX_LENGTH,
        },
        description: {
            maxLength: DOCUMENT_DESCRIPTION_MAX_LENGTH,
        },
        summary: {
            maxLength: DOCUMENT_SUMMARY_MAX_LENGTH,
        },
    };

    //Case constants
    const CASE_NAME_MAX_LENGTH = 75;
    const CASE_TYPE_MAX_LENGTH = 75;
    const CASE_SHORT_DESCRIPTION_MAX_LENGTH = 200;
    const CASE_OVERVIEW_MAX_LENGTH = 2000;
    const CASE_RETAINED_DATE_MIN = new Date(2020, 0, 1);
    const CASE_RETAINED_DATE_MAX = new Date();
    CASE_RETAINED_DATE_MAX.setDate(CASE_RETAINED_DATE_MAX.getDate() + 90);
    CASE_RETAINED_DATE_MAX.setHours(23, 59, 59, 999);

    const validateCaseObj = {
        schema: yup.object<Case>({
            caseName: yup
                .string()
                .max(CASE_NAME_MAX_LENGTH, `Max ${CASE_NAME_MAX_LENGTH} characters`)
                .required('Case Name is a required field.'),
            caseType: yup.string().max(CASE_TYPE_MAX_LENGTH, `Max ${CASE_TYPE_MAX_LENGTH} characters`),
            shortDescription: fieldEntrySchema(CASE_SHORT_DESCRIPTION_MAX_LENGTH),
            retainedDate: yup
                .date()
                .min(CASE_RETAINED_DATE_MIN, `Date must be after ${CASE_RETAINED_DATE_MIN.toLocaleDateString()}`)
                .max(CASE_RETAINED_DATE_MAX, 'Date cannot be more than 90 days in the future.'),
            // retainedBy: validatePartyWithContacts.schema,
            overview: yup.string().max(CASE_OVERVIEW_MAX_LENGTH, `Max ${CASE_OVERVIEW_MAX_LENGTH} characters`).nullable(),
            isFixedFee: yup.boolean(),
            fixedAmount: yup.string().matches(/^[0-9.]*$/, 'Contains invalid characters'),
            estimatedHours: yup
                .string()
                .matches(/^[0-9.]*$/, 'Contains invalid characters')
                .nullable(),
            affiliatedParties: yup.array(validatePartyWithContacts.schema),
            caseEvents: yup.array(validateCaseEvent.schema),
            caseNotes: yup.array(validateCaseNote.schema),
        }),
        caseName: { maxLength: CASE_NAME_MAX_LENGTH },
        caseType: { maxLength: CASE_TYPE_MAX_LENGTH },
        shortDescription: { maxLength: CASE_SHORT_DESCRIPTION_MAX_LENGTH },
        overview: { maxLength: CASE_OVERVIEW_MAX_LENGTH },
        retainedDate: { min: CASE_RETAINED_DATE_MIN, max: CASE_RETAINED_DATE_MAX },
    };

    const validateCaseType = {
        schema: yup.object<{ type: string }>({
            type: pathSegmentSafe(
                yup
                    .string()
                    .max(CASE_TYPE_MAX_LENGTH, `Max ${CASE_TYPE_MAX_LENGTH} characters`)
                    .matches(/^[a-zA-Z0-9\s\-_.]+$/, 'Contains invalid characters'),
            ),
        }),
        type: { maxLength: CASE_TYPE_MAX_LENGTH },
    };

    //Task constants
    const TASK_TEXT_MAX_LENGTH = 500;
    const TASK_RESULTS_MAX_LENGTH = 800;
    const TASK_TITLE_MAX_LENGTH = 150;
    const TASK_CATEGORY_MAX_LENGTH = 100;

    const validateTask = {
        schema: yup.object<Task>({
            userId: yup.string().required('Assigned To is a required field'),
            caseId: yup.string().required('Case is a required field'),
            milestoneId: yup.string().nullable(),
            title: yup
                .string()
                .max(TASK_TITLE_MAX_LENGTH, `Max ${TASK_TITLE_MAX_LENGTH} characters`)
                .required('Task title is a required field.'),
            text: yup
                .string()
                .max(TASK_TEXT_MAX_LENGTH, `Max ${TASK_TEXT_MAX_LENGTH} characters`)
                .required('Description is a required field.'),
            results: yup.string().max(TASK_RESULTS_MAX_LENGTH, `Max ${TASK_RESULTS_MAX_LENGTH} characters`).nullable(),
            category: yup.string().max(TASK_CATEGORY_MAX_LENGTH, `Max ${TASK_CATEGORY_MAX_LENGTH} characters`).nullable(),
            status: yup.mixed<TaskStatus>().oneOf(Object.values(TaskStatus) as TaskStatus[]),
            billable: yup.boolean(),
        }),
        text: { maxLength: TASK_TEXT_MAX_LENGTH },
        results: { maxLength: TASK_RESULTS_MAX_LENGTH },
        title: { maxLength: TASK_TITLE_MAX_LENGTH },
        category: { maxLength: TASK_CATEGORY_MAX_LENGTH },
    };

    //Time Entry constants
    const TIME_ENTRY_DESCRIPTION_MAX_LENGTH = 500;

    const validateTimeTrackEntry = {
        schema: yup.object<TimeTrackingEntry>({
            hours: yup
                .number()
                .min(0.0, 'Hours cannot be negative')
                .max(24, 'Cannot log more than 24 hours in a single entry')
                .required('Hours is a required field.'),
            description: yup
                .string()
                .max(TIME_ENTRY_DESCRIPTION_MAX_LENGTH, `Max ${TIME_ENTRY_DESCRIPTION_MAX_LENGTH} characters`)
                .required('Description is a required field.'),
            isBillable: yup.boolean(),
        }),
        description: { maxLength: TIME_ENTRY_DESCRIPTION_MAX_LENGTH },
    };

    //Case Expense constants
    const CASE_EXPENSE_TEXT_MAX_LENGTH = 200;

    const validateCaseExpense = {
        schema: yup.object<ExpenseWithAttachment>({
            attachment: yup.mixed<File>().nullable(),
            expense: yup.object({
                username: yup.string(),
                text: yup
                    .string()
                    .max(CASE_EXPENSE_TEXT_MAX_LENGTH, `Max ${CASE_EXPENSE_TEXT_MAX_LENGTH} characters`)
                    .required('Description is a required field.'),
                amount: yup
                    .string()
                    .matches(/^[0-9.]*$/, 'Contains invalid characters')
                    .required('Amount is a required field.'),
                date: yup.date().required('Date is a required field.'),
                type: yup.string().required('Expense Type is a required field.'),
                attachmentId: yup.string().nullable(),
                attachmentName: yup.string().nullable(),
            }),
        }),
        text: { maxLength: CASE_EXPENSE_TEXT_MAX_LENGTH },
    };

    const CASE_EXPENSE_TYPE_TEXT_MAX_LENGTH = 50;
    const validateCaseExpenseType = {
        schema: yup.object<{ expenseType: string }>({
            expenseType: pathSegmentSafe(
                yup
                    .string()
                    .max(CASE_EXPENSE_TYPE_TEXT_MAX_LENGTH, `Max ${CASE_EXPENSE_TYPE_TEXT_MAX_LENGTH} characters`)
                    .matches(/^[a-zA-Z0-9\s\-_.]+$/, 'Contains invalid characters'),
            ),
        }),
        text: { maxLength: CASE_EXPENSE_TYPE_TEXT_MAX_LENGTH },
    };

    //Case Engagement constants
    const ENGAGEMENT_TITLE_MAX_LENGTH = 50;

    const validateCaseEngagement = {
        schema: yup.object<CaseEngagementDoc>({
            title: yup.string().max(ENGAGEMENT_TITLE_MAX_LENGTH, `Max ${ENGAGEMENT_TITLE_MAX_LENGTH} characters`),
            paymentBy: yup.string(),
            addSignatures: yup.boolean(),
            fields: yup.array(fieldEntrySchema()),
            accountSignatures: yup.array(
                yup.object({
                    userId: yup.string().required(),
                    username: yup.string().required(),
                    name: yup.string(),
                    hourlyRate: yup.string(),
                    clientBillRate: yup.string(),
                    userPayRate: yup.string(),
                }),
            ),
            clientSignatures: yup.array(validateContact.schema),
        }),
        title: { maxLength: ENGAGEMENT_TITLE_MAX_LENGTH },
    };

    //Engagement Template constants
    const ENGAGEMENT_TEMPLATE_NAME_MAX_LENGTH = 75;
    const ENGAGEMENT_TEMPLATE_DESCRIPTION_MAX_LENGTH = 200;

    const validateEngagementTemplate = {
        schema: yup.object<EngagementTemplate>({
            templateName: yup
                .string()
                .max(ENGAGEMENT_TEMPLATE_NAME_MAX_LENGTH, `Max ${ENGAGEMENT_TEMPLATE_NAME_MAX_LENGTH} characters`)
                .required('Template Name is a required field.'),
            title: yup
                .string()
                .max(ENGAGEMENT_TITLE_MAX_LENGTH, `Max ${ENGAGEMENT_TITLE_MAX_LENGTH} characters`)
                .required('Title is a required field.'),
            description: yup
                .string()
                .max(
                    ENGAGEMENT_TEMPLATE_DESCRIPTION_MAX_LENGTH,
                    `Max ${ENGAGEMENT_TEMPLATE_DESCRIPTION_MAX_LENGTH} characters`,
                )
                .required('Template Description is a required field.'),
            addSignatures: yup.boolean(),
            fields: yup.array(fieldEntrySchema()),
        }),
        templateName: { maxLength: ENGAGEMENT_TEMPLATE_NAME_MAX_LENGTH },
        description: { maxLength: ENGAGEMENT_TEMPLATE_DESCRIPTION_MAX_LENGTH },
        title: { maxLength: ENGAGEMENT_TITLE_MAX_LENGTH },
    };

    //Case Event Template constants
    const CASE_EVENT_TEMPLATE_NAME_MAX_LENGTH = 50;
    const CASE_EVENT_TEMPLATE_DESCRIPTION_MAX_LENGTH = 200;

    const validateCaseEventTemplate = {
        schema: yup.object<CaseEventTemplate>({
            eventName: yup
                .string()
                .max(CASE_EVENT_TEMPLATE_NAME_MAX_LENGTH, `Max ${CASE_EVENT_TEMPLATE_NAME_MAX_LENGTH} characters`)
                .required('Event Name is a required field.'),
            description: yup
                .string()
                .max(
                    CASE_EVENT_TEMPLATE_DESCRIPTION_MAX_LENGTH,
                    `Max ${CASE_EVENT_TEMPLATE_DESCRIPTION_MAX_LENGTH} characters`,
                )
                .nullable(),
            daysFromRetained: yup.number(),
            due: yup.boolean(),
        }),
        eventName: { maxLength: CASE_EVENT_TEMPLATE_NAME_MAX_LENGTH },
        description: { maxLength: CASE_EVENT_TEMPLATE_DESCRIPTION_MAX_LENGTH },
    };

    const validateTaskTemplate = {
        schema: yup.object<TaskTemplate>({
            username: yup.string(),
            userId: yup.string(),
            title: yup
                .string()
                .max(TASK_TITLE_MAX_LENGTH, `Max ${TASK_TITLE_MAX_LENGTH} characters`)
                .required('Task title is a required field.'),
            text: yup
                .string()
                .max(TASK_TEXT_MAX_LENGTH, `Max ${TASK_TEXT_MAX_LENGTH} characters`)
                .required('Task text is a required field.'),
            billable: yup.boolean(),
        }),
        text: { maxLength: TASK_TEXT_MAX_LENGTH },
        title: { maxLength: TASK_TITLE_MAX_LENGTH },
    };

    //Case Milestone constants
    const CASE_MILESTONE_NAME_MAX_LENGTH = 50;
    const CASE_MILESTONE_DESCRIPTION_MAX_LENGTH = 800;
    const CASE_MILESTONE_DELIVERABLES_MAX_LENGTH = 1000;
    const CASE_MILESTONE_DEPENDENCIES_MAX_LENGTH = 800;
    const CASE_MILESTONE_CURRENT_BLOCKERS_MAX_LENGTH = 800;
    const CASE_MILESTONE_DEFINITION_OF_DONE_MAX_LENGTH = 800;
    const CASE_MILESTONE_RESULT_SUMMARY_MAX_LENGTH = 800;

    const validateCaseMilestone = {
        schema: yup.object<CaseMilestone>({
            name: yup
                .string()
                .max(CASE_MILESTONE_NAME_MAX_LENGTH, `Max ${CASE_MILESTONE_NAME_MAX_LENGTH} characters`)
                .matches(/^[a-zA-Z0-9\s\-_.,]+$/, 'Contains invalid characters')
                .required('Milestone name is a required field.'),
            description: yup
                .string()
                .max(CASE_MILESTONE_DESCRIPTION_MAX_LENGTH, `Max ${CASE_MILESTONE_DESCRIPTION_MAX_LENGTH} characters`)
                .nullable(),
            deliverablesDelivered: yup.boolean(),
            deliverables: yup
                .string()
                .max(CASE_MILESTONE_DELIVERABLES_MAX_LENGTH, `Max ${CASE_MILESTONE_DELIVERABLES_MAX_LENGTH} characters`)
                .nullable(),
            externalDependencies: yup
                .string()
                .max(CASE_MILESTONE_DEPENDENCIES_MAX_LENGTH, `Max ${CASE_MILESTONE_DEPENDENCIES_MAX_LENGTH} characters`)
                .nullable(),
            category: yup.string().nullable(),
            priority: yup.number().nullable(),
            estimatedHoursCost: yup.string().nullable(),
            actualHoursCost: yup.string().nullable(),
            budgetVarianceReason: yup.string().nullable(),
            definitionOfDone: yup
                .string()
                .max(
                    CASE_MILESTONE_DEFINITION_OF_DONE_MAX_LENGTH,
                    `Max ${CASE_MILESTONE_DEFINITION_OF_DONE_MAX_LENGTH} characters`,
                )
                .nullable(),
            resultSummary: yup
                .string()
                .max(CASE_MILESTONE_RESULT_SUMMARY_MAX_LENGTH, `Max ${CASE_MILESTONE_RESULT_SUMMARY_MAX_LENGTH} characters`)
                .nullable(),
            currentBlockers: yup
                .string()
                .max(
                    CASE_MILESTONE_CURRENT_BLOCKERS_MAX_LENGTH,
                    `Max ${CASE_MILESTONE_CURRENT_BLOCKERS_MAX_LENGTH} characters`,
                )
                .nullable(),
            dueDate: yup.date().nullable(),
            escalationDate: yup.date().nullable(),
            completed: yup.boolean(),
            completedOn: yup.date().nullable(),
            completedBy: yup.string().nullable(),
            order: yup.number(),
        }),
        name: { maxLength: CASE_MILESTONE_NAME_MAX_LENGTH },
        description: { maxLength: CASE_MILESTONE_DESCRIPTION_MAX_LENGTH },
        deliverables: { maxLength: CASE_MILESTONE_DELIVERABLES_MAX_LENGTH },
        externalDependencies: { maxLength: CASE_MILESTONE_DEPENDENCIES_MAX_LENGTH },
        currentBlockers: { maxLength: CASE_MILESTONE_CURRENT_BLOCKERS_MAX_LENGTH },
        definitionOfDone: { maxLength: CASE_MILESTONE_DEFINITION_OF_DONE_MAX_LENGTH },
        resultSummary: { maxLength: CASE_MILESTONE_RESULT_SUMMARY_MAX_LENGTH },
    };

    const validateCaseMilestoneTemplate = {
        schema: yup.object<CaseMilestoneTemplate>({
            name: yup
                .string()
                .max(CASE_MILESTONE_NAME_MAX_LENGTH, `Max ${CASE_MILESTONE_NAME_MAX_LENGTH} characters`)
                .matches(/^[a-zA-Z0-9\s\-_.]+$/, 'Contains invalid characters')
                .required('Milestone name is a required field.'),
            description: yup
                .string()
                .max(CASE_MILESTONE_DESCRIPTION_MAX_LENGTH, `Max ${CASE_MILESTONE_DESCRIPTION_MAX_LENGTH} characters`)
                .nullable(),
            deliverables: yup
                .string()
                .max(CASE_MILESTONE_DELIVERABLES_MAX_LENGTH, `Max ${CASE_MILESTONE_DELIVERABLES_MAX_LENGTH} characters`)
                .nullable(),
            dueDaysFromCreated: yup.number(),
            events: yup.array(validateCaseEventTemplate.schema),
            tasks: yup.array(validateTaskTemplate.schema),
        }),
        name: { maxLength: CASE_MILESTONE_NAME_MAX_LENGTH },
        description: { maxLength: CASE_MILESTONE_DESCRIPTION_MAX_LENGTH },
        deliverables: { maxLength: CASE_MILESTONE_DELIVERABLES_MAX_LENGTH },
    };

    //Case Template constants
    const CASE_TEMPLATE_NAME_MAX_LENGTH = 75;
    const CASE_TEMPLATE_DESCRIPTION_MAX_LENGTH = 200;

    const validateCaseTemplate = {
        schema: yup.object<CaseTemplate>({
            templateName: yup
                .string()
                .max(CASE_TEMPLATE_NAME_MAX_LENGTH, `Max ${CASE_TEMPLATE_NAME_MAX_LENGTH} characters`)
                .required('Template Name is a required field.'),
            templateDescription: yup
                .string()
                .max(CASE_TEMPLATE_DESCRIPTION_MAX_LENGTH, `Max ${CASE_TEMPLATE_DESCRIPTION_MAX_LENGTH} characters`)
                .required('Template Description is a required field.'),
            caseType: yup.string().max(CASE_TYPE_MAX_LENGTH, `Max ${CASE_TYPE_MAX_LENGTH} characters`),
            overview: yup.string().max(CASE_OVERVIEW_MAX_LENGTH, `Max ${CASE_OVERVIEW_MAX_LENGTH} characters`),
        }),
        templateName: { maxLength: CASE_TEMPLATE_NAME_MAX_LENGTH },
        templateDescription: { maxLength: CASE_TEMPLATE_DESCRIPTION_MAX_LENGTH },
        overview: { maxLength: CASE_OVERVIEW_MAX_LENGTH },
    };

    const validateFeatureSet = {
        schema: yup.object<FeatureSet>({
            seats: yup.number(),
            clientsAndContacts: yup.boolean(),
            caseManagement: yup.boolean(),
            engagement: yup.boolean(),
            milestones: yup.boolean(),
            tasks: yup.boolean(),
            events: yup.boolean(),
            notes: yup.boolean(),
            documents: yup.boolean(),
            documentLimit: yup.number(),
            timeTracking: yup.boolean(),
            expenseManagement: yup.boolean(),
            invoices: yup.boolean(),
            templates: yup.boolean(),
            insights: yup.boolean(),
            notifications: yup.boolean(),
            clientPortal: yup.boolean(),
            copilot: yup.boolean(),
            integrations: yup.boolean(),
            reporting: yup.boolean(),
            auditHistory: yup.boolean(),
        }),
    };

    const validateMetricDisplay = {
        schema: yup.object<MetricDisplay>({
            use: yup.boolean(),
            warningBelow: yup.number(),
            dangerBelow: yup.number(),
        }),
    };

    const validateAccountSettings = {
        schema: yup.object<AccountSettings>({
            billingTimePercentDisplay: validateMetricDisplay.schema.required(),
            groupStatuses: yup.boolean(),
            mustUseTemplateToCreateEngagement: yup.boolean(),
            timePeriodsForInsights: yup.array(yup.string().required()),
            requireTasksBeTiedToMilestones: yup.boolean(),
            cannotCompleteMilestoneBeforePrevious: yup.boolean(),
            notifyAdminsWhenCaseCreated: yup.boolean(),
            notifyAdminsWhenCaseStatusChanges: yup.boolean(),
            notifyAdminsWhenCaseDeleted: yup.boolean(),
            notifyAdminsWhenCaseEngagementChanges: yup.boolean(),
            notifyAdminsWhenCaseMilestoneChanges: yup.boolean(),
            notifyCaseLeadsWhenTaskCreatedWithoutMilestone: yup.boolean(),
        }),
    };

    //Invoice constants
    const INVOICE_NUMBER_MAX_LENGTH = 50;

    const validateInvoice = {
        schema: yup.object<Invoice>({
            invoiceNumber: yup
                .string()
                .max(INVOICE_NUMBER_MAX_LENGTH, `Max ${INVOICE_NUMBER_MAX_LENGTH} characters`)
                .required('Invoice Number is a required field.'),
            invoiceDate: yup.date().required('Invoice Date is a required field.'),
            billingPeriodStart: yup.date().nullable(),
            billingPeriodEnd: yup.date().nullable(),
            dueDate: yup.date().nullable(),
            // billableTimeEntries: yup.array(validateTimeTrackEntry.schema),
            // expenses: yup.array(validateCaseExpense.schema),
            fixedAmount: yup.string().nullable(),
            attachEngagement: yup.boolean(),
            paymentBy: yup.string().nullable(),
            isPaid: yup.boolean(),
            paidDate: yup.date().nullable(),
        }),
        invoiceNumber: { maxLength: INVOICE_NUMBER_MAX_LENGTH },
    };

    const validateFileUploadInfo = {
        schema: yup.object<FileUploadInfo>({
            fileName: yup.string().required(),
            file: yup.mixed<File>(),
            fileSize: yup.number().required(),
            progress: yup.number().required(),
            uploadTime: yup.number().required(),
            uploadStarted: yup.boolean().required(),
            uploadComplete: yup.boolean().required(),
            otherUpload: yup.boolean(),
        }),
    };

    const validateLogin = {
        schema: yup.object({
            username: yup
                .string()
                .max(PERSON_EMAIL_MAX_LENGTH, `Max ${PERSON_EMAIL_MAX_LENGTH} characters`)
                .required('Username is a required field.'),
            password: yup.string().required('Password is a required field.'),
        }),
        username: { maxLength: PERSON_EMAIL_MAX_LENGTH },
    };

    const validateRequestPWReset = {
        schema: yup.object({
            username: yup
                .string()
                .max(PERSON_EMAIL_MAX_LENGTH, `Max ${PERSON_EMAIL_MAX_LENGTH} characters`)
                .required('Username is a required field.'),
        }),
        username: { maxLength: PERSON_EMAIL_MAX_LENGTH },
    };

    const RESET_CODE_MAX_LENGTH = 12;
    // Mirrors the server-side password policy.
    const PASSWORD_MIN_LENGTH = 12;
    const PASSWORD_MAX_LENGTH = 256;
    const PASSWORD_LENGTH_MESSAGE = `Password must be between ${PASSWORD_MIN_LENGTH} and ${PASSWORD_MAX_LENGTH} characters.`;
    const PASSWORD_COMPLEXITY_MESSAGE = 'Password must include uppercase, lowercase, number, and symbol characters.';

    const validateResetPW = {
        schema: yup.object({
            resetCode: yup
                .string()
                .max(RESET_CODE_MAX_LENGTH, `Max ${RESET_CODE_MAX_LENGTH} characters`)
                .required('Reset code is required.'),
            newPassword: yup
                .string()
                .required('Password is required.')
                .min(PASSWORD_MIN_LENGTH, PASSWORD_LENGTH_MESSAGE)
                .max(PASSWORD_MAX_LENGTH, PASSWORD_LENGTH_MESSAGE)
                .matches(/[A-Z]/, PASSWORD_COMPLEXITY_MESSAGE)
                .matches(/[a-z]/, PASSWORD_COMPLEXITY_MESSAGE)
                .matches(/[0-9]/, PASSWORD_COMPLEXITY_MESSAGE)
                .matches(/[^a-zA-Z0-9]/, PASSWORD_COMPLEXITY_MESSAGE),
            confirmPassword: yup
                .string()
                .required('Please confirm your new password.')
                .oneOf([yup.ref('newPassword')], 'Passwords do not match.'),
        }),
        resetCode: { maxLength: RESET_CODE_MAX_LENGTH },
        newPassword: { maxLength: PASSWORD_MAX_LENGTH, minLength: PASSWORD_MIN_LENGTH },
    };

    return {
        validateAccount,
        validateAddress,
        validatePhone,
        validateIndividual,
        validateUser,
        validateContact,
        validateParty,
        validatePartyWithContacts,
        validateCaseEvent,
        validateCaseNote,
        validateCaseDocument,
        validateCaseObj,
        validateCaseType,
        validateCaseStatus,
        validateFieldEntry,
        validateTask,
        validateTimeTrackEntry,
        validateCaseExpense,
        validateCaseExpenseType,
        validateCaseEngagement,
        validateEngagementTemplate,
        validateCaseTemplate,
        validateFileUploadInfo,
        validateCaseMilestone,
        validateCaseMilestoneTemplate,
        validateTaskTemplate,
        validateCaseEventTemplate,
        validateFeatureSet,
        validateAccountSettings,
        validateMetricDisplay,
        validateInvoice,
        validateLogin,
        validateRequestPWReset,
        validateResetPW,
    };
};
